import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useFilterStore } from '../store/filterStore';
import { loyverseApi, layoutApi, targetApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, startOfMonth, startOfYear, subDays, subMonths, subWeeks, subYears, endOfWeek, endOfMonth, endOfYear } from 'date-fns';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import {
  CalendarIcon, Filter, TrendingUp, TrendingDown, Package, DollarSign, ShoppingCart,
  Users, BarChart3, LineChartIcon, Save, RefreshCw, Loader2, X, ChevronDown, Target,
  Database, CloudDownload, Monitor, Sparkles, Split, Layers, GitCompare, RotateCcw, Percent
} from 'lucide-react';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { formatNumber } from '../lib/utils';

const Dashboard = () => {
  const { selectedBusinessId, canViewRevenue, canManageBusinesses } = useAuthStore();
  const filterStore = useFilterStore();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  
  // Reference data for filters
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [posDevices, setPosDevices] = useState([]);
  
  // Sync status
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Targets
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  
  const [showFilters, setShowFilters] = useState(false);
  const [saveLayoutOpen, setSaveLayoutOpen] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [showComparisonDialog, setShowComparisonDialog] = useState(false);
  
  // AI Insights state
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showInsights, setShowInsights] = useState(false);

  // Fetch sync status and trigger auto-sync if needed
  useEffect(() => {
    if (!selectedBusinessId) return;
    
    const checkSyncStatus = async () => {
      try {
        const response = await loyverseApi.getSyncStatus(selectedBusinessId);
        setSyncStatus(response.data);
        
        // Auto-sync on first load if no data cached
        if (!response.data.last_synced_at && !response.data.sync_in_progress) {
          handleStartSync();
        }
      } catch (error) {
        console.error('Failed to fetch sync status:', error);
      }
    };
    
    checkSyncStatus();
    
    // Poll sync status while syncing
    const interval = setInterval(async () => {
      if (syncing) {
        try {
          const response = await loyverseApi.getSyncStatus(selectedBusinessId);
          setSyncStatus(response.data);
          if (!response.data.sync_in_progress && response.data.last_synced_at) {
            setSyncing(false);
            toast.success(`Sync complete! ${formatNumber(response.data.total_receipts)} receipts cached.`);
            fetchDashboardData(true);
          }
        } catch (error) {
          console.error('Failed to poll sync status:', error);
        }
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [selectedBusinessId, syncing]);

  // Fetch reference data
  useEffect(() => {
    if (!selectedBusinessId) return;
    
    const fetchReferenceData = async () => {
      try {
        const [storesRes, employeesRes, categoriesRes, itemsRes, paymentTypesRes, discountsRes, modifiersRes, posDevicesRes] = await Promise.all([
          loyverseApi.getStores(selectedBusinessId),
          loyverseApi.getEmployees(selectedBusinessId),
          loyverseApi.getCategories(selectedBusinessId),
          loyverseApi.getItems(selectedBusinessId),
          loyverseApi.getPaymentTypes(selectedBusinessId),
          loyverseApi.getDiscounts(selectedBusinessId),
          loyverseApi.getModifiers(selectedBusinessId),
          loyverseApi.getPosDevices(selectedBusinessId).catch(() => ({ data: [] })),
        ]);
        
        setStores(storesRes.data || []);
        setEmployees(employeesRes.data || []);
        setCategories(categoriesRes.data || []);
        setItems(itemsRes.data || []);
        setPaymentTypes(paymentTypesRes.data || []);
        setDiscounts(discountsRes.data || []);
        setModifiers(modifiersRes.data || []);
        setPosDevices(posDevicesRes.data || []);
      } catch (error) {
        console.error('Failed to fetch reference data:', error);
        toast.error('Failed to load filter options');
      }
    };
    
    fetchReferenceData();
  }, [selectedBusinessId]);

  // Handle start sync
  const handleStartSync = async () => {
    if (!selectedBusinessId || syncing) return;
    
    try {
      setSyncing(true);
      toast.info('Starting data sync from Loyverse...');
      await loyverseApi.startSync(selectedBusinessId);
    } catch (error) {
      console.error('Failed to start sync:', error);
      toast.error('Failed to start sync');
      setSyncing(false);
    }
  };

  // Fetch targets for overlay
  useEffect(() => {
    if (!selectedBusinessId || !filterStore.startDate) return;
    
    const fetchTargets = async () => {
      try {
        const startDate = parseISO(filterStore.startDate);
        const params = { year: startDate.getFullYear(), month: startDate.getMonth() + 1 };
        const response = await targetApi.getAll(selectedBusinessId, params);
        setTargets(response.data || []);
      } catch (error) {
        console.error('Failed to fetch targets:', error);
      }
    };
    
    fetchTargets();
  }, [selectedBusinessId, filterStore.startDate]);

  // Fetch dashboard data
  const fetchDashboardData = async (isRefresh = false) => {
    if (!selectedBusinessId) return;
    
    isRefresh ? setRefreshing(true) : setLoading(true);
    
    try {
      const filters = filterStore.getFilterPayload();
      const response = await loyverseApi.getDashboard(selectedBusinessId, filters);
      setDashboardData(response.data);
      
      // Fetch comparison data if enabled
      if (filterStore.comparisonEnabled && filterStore.comparisonStartDate && filterStore.comparisonEndDate) {
        const compFilters = {
          ...filters,
          start_date: `${filterStore.comparisonStartDate}T00:00:00Z`,
          end_date: `${filterStore.comparisonEndDate}T23:59:59Z`,
        };
        
        const compResponse = await loyverseApi.getDashboard(selectedBusinessId, compFilters);
        setComparisonData(compResponse.data);
      } else {
        setComparisonData(null);
      }
      
      if (isRefresh) toast.success('Data refreshed');
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedBusinessId, filterStore.startDate, filterStore.endDate, filterStore.comparisonEnabled, filterStore.comparisonStartDate, filterStore.comparisonEndDate]);

  // Group data by period
  const groupedData = useMemo(() => {
    if (!dashboardData?.daily_data) return [];
    
    const data = dashboardData.daily_data;
    const groupBy = filterStore.groupBy;
    
    if (groupBy === 'day') return data;
    
    const grouped = {};
    data.forEach((item) => {
      const date = parseISO(item.date);
      let key;
      
      if (groupBy === 'week') {
        key = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else if (groupBy === 'month') {
        key = format(startOfMonth(date), 'yyyy-MM');
      } else if (groupBy === 'year') {
        key = format(startOfYear(date), 'yyyy');
      }
      
      if (!grouped[key]) {
        grouped[key] = { date: key, quantity: 0, revenue: 0 };
      }
      grouped[key].quantity += item.quantity;
      grouped[key].revenue += item.revenue;
    });
    
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [dashboardData, filterStore.groupBy]);

  // Group comparison data the same way as main data
  const groupedComparisonData = useMemo(() => {
    if (!comparisonData?.daily_data) return [];
    
    const data = comparisonData.daily_data;
    const groupBy = filterStore.groupBy;
    
    if (groupBy === 'day') return data;
    
    const grouped = {};
    data.forEach((item) => {
      const date = parseISO(item.date);
      let key;
      
      if (groupBy === 'week') {
        key = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else if (groupBy === 'month') {
        key = format(startOfMonth(date), 'yyyy-MM');
      } else if (groupBy === 'year') {
        key = format(startOfYear(date), 'yyyy');
      }
      
      if (!grouped[key]) {
        grouped[key] = { date: key, quantity: 0, revenue: 0 };
      }
      grouped[key].quantity += item.quantity;
      grouped[key].revenue += item.revenue;
    });
    
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [comparisonData, filterStore.groupBy]);

  // Helper to format date labels based on groupBy setting
  const formatDateLabel = (val) => {
    if (!val) return '';
    try {
      const groupBy = filterStore.groupBy;
      if (groupBy === 'day') return format(parseISO(val), 'MMM d');
      if (groupBy === 'week') return `W${format(parseISO(val), 'w')}`;
      if (groupBy === 'month') {
        // Month format is 'yyyy-MM', need to add day for parsing
        return format(parseISO(val + '-01'), 'MMM');
      }
      if (groupBy === 'year') return val;
      return val;
    } catch { 
      return val; 
    }
  };

  // Calculate comparison percentages
  const getComparison = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return change;
  };

  // Save layout handler
  const handleSaveLayout = async () => {
    if (!layoutName.trim()) {
      toast.error('Please enter a layout name');
      return;
    }
    
    try {
      const config = filterStore.getLayoutConfig();
      await layoutApi.create({ name: layoutName, ...config });
      toast.success('Layout saved successfully');
      setSaveLayoutOpen(false);
      setLayoutName('');
    } catch (error) {
      toast.error('Failed to save layout');
    }
  };

  // Apply filters handler
  const handleApplyFilters = () => {
    fetchDashboardData(true);
    setShowFilters(false);
  };

  // Generate AI Insights
  const handleGenerateInsights = async () => {
    if (!dashboardData) return;
    
    setLoadingInsights(true);
    try {
      const response = await loyverseApi.getInsights(selectedBusinessId, {
        dashboard_data: dashboardData,
        filters_applied: filterStore.getFilterPayload(),
        insight_types: ['summary', 'trends', 'anomalies', 'recommendations']
      });
      setInsights(response.data.insights);
      setShowInsights(true);
    } catch (error) {
      console.error('Failed to generate insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  // Build split view data for charts (with groupBy support)
  const splitViewData = useMemo(() => {
    if (!dashboardData?.by_store || !filterStore.splitView) return null;
    
    const byStore = dashboardData.by_store;
    const storeNames = dashboardData.store_names || {};
    const groupBy = filterStore.groupBy;
    
    // First, collect all dates and group them if needed
    const allDates = new Set();
    Object.values(byStore).forEach(storeDates => {
      Object.keys(storeDates).forEach(date => allDates.add(date));
    });
    
    // Build daily data first
    const sortedDates = Array.from(allDates).sort();
    const dailyData = sortedDates.map(date => {
      const row = { date };
      Object.entries(byStore).forEach(([storeId, storeDates]) => {
        const dayData = storeDates[date] || { quantity: 0, revenue: 0 };
        const storeName = storeNames[storeId] || stores.find(s => s.id === storeId)?.name || storeId.slice(0, 8);
        row[`${storeName}_qty`] = dayData.quantity;
        row[`${storeName}_rev`] = dayData.revenue;
      });
      return row;
    });
    
    // If groupBy is 'day', return daily data
    if (groupBy === 'day') return dailyData;
    
    // Group the data by week/month/year
    const grouped = {};
    const storeNamesList = Object.entries(byStore).map(([storeId]) => 
      storeNames[storeId] || stores.find(s => s.id === storeId)?.name || storeId.slice(0, 8)
    );
    
    dailyData.forEach((item) => {
      const date = parseISO(item.date);
      let key;
      
      if (groupBy === 'week') {
        key = format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else if (groupBy === 'month') {
        key = format(startOfMonth(date), 'yyyy-MM');
      } else if (groupBy === 'year') {
        key = format(startOfYear(date), 'yyyy');
      }
      
      if (!grouped[key]) {
        grouped[key] = { date: key };
        storeNamesList.forEach(name => {
          grouped[key][`${name}_qty`] = 0;
          grouped[key][`${name}_rev`] = 0;
        });
      }
      
      storeNamesList.forEach(name => {
        grouped[key][`${name}_qty`] += item[`${name}_qty`] || 0;
        grouped[key][`${name}_rev`] += item[`${name}_rev`] || 0;
      });
    });
    
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [dashboardData, filterStore.splitView, filterStore.groupBy, stores]);

  // Get store names for split view legend
  const splitViewStores = useMemo(() => {
    if (!dashboardData?.by_store) return [];
    const storeNames = dashboardData.store_names || {};
    return Object.keys(dashboardData.by_store).map(storeId => {
      const name = storeNames[storeId] || stores.find(s => s.id === storeId)?.name || storeId.slice(0, 8);
      return { id: storeId, name };
    });
  }, [dashboardData, stores]);

  // Chart colors for split view
  const chartColors = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7300',
    '#00C49F'
  ];

  if (!selectedBusinessId) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <Package className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="font-serif text-2xl font-semibold mb-2">Select a Business</h2>
        <p className="text-muted-foreground">
          Choose a business from the dropdown above to view the dashboard
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sync Status Banner */}
      {syncStatus && (
        <Card className={`${syncing ? 'border-blue-500 bg-blue-50' : syncStatus.last_synced_at ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}`}>
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Database className={`w-5 h-5 ${syncing ? 'text-blue-600 animate-pulse' : syncStatus.last_synced_at ? 'text-green-600' : 'text-yellow-600'}`} />
                <div>
                  {syncing ? (
                    <p className="text-sm font-medium text-blue-800">Syncing data from Loyverse...</p>
                  ) : syncStatus.last_synced_at ? (
                    <p className="text-sm font-medium text-green-800">
                      {formatNumber(syncStatus.total_receipts)} receipts cached • Last synced {format(parseISO(syncStatus.last_synced_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  ) : (
                    <p className="text-sm font-medium text-yellow-800">No cached data - Click &quot;Sync Now&quot; to download historical data</p>
                  )}
                </div>
              </div>
              {canManageBusinesses() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartSync}
                  disabled={syncing}
                  className="rounded-lg"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <CloudDownload className="w-4 h-4 mr-2" />
                      Sync Now
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-coffee-espresso">
            Sales Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            {filterStore.startDate && filterStore.endDate
              ? `${format(parseISO(filterStore.startDate), 'MMM d, yyyy')} - ${format(parseISO(filterStore.endDate), 'MMM d, yyyy')}`
              : 'Current Month'}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle"
            className="rounded-xl"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {(filterStore.storeIds.length > 0 || filterStore.employeeIds.length > 0 || filterStore.posDeviceIds.length > 0) && (
              <Badge variant="secondary" className="ml-2 rounded-full">
                Active
              </Badge>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
            className="rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Dialog open={saveLayoutOpen} onOpenChange={setSaveLayoutOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-xl" data-testid="save-layout-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Layout
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Layout</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <Label htmlFor="layoutName">Layout Name</Label>
                <Input
                  id="layoutName"
                  value={layoutName}
                  onChange={(e) => setLayoutName(e.target.value)}
                  placeholder="My Custom Layout"
                  className="mt-2"
                  data-testid="layout-name-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSaveLayoutOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveLayout} data-testid="confirm-save-layout">
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="animate-fade-in">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Filters</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowFilters(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Row 1: Date Range, Branch, POS Device, Employee */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date Range */}
              <div className="space-y-2">
                <Label>Date Range</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 justify-start text-left font-normal text-sm" data-testid="start-date-picker">
                        <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{filterStore.startDate ? format(parseISO(filterStore.startDate), 'MMM d, yyyy') : 'Start'}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filterStore.startDate ? parseISO(filterStore.startDate) : undefined}
                        onSelect={(date) => filterStore.setDateRange(
                          date ? format(date, 'yyyy-MM-dd') : null,
                          filterStore.endDate
                        )}
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 justify-start text-left font-normal text-sm" data-testid="end-date-picker">
                        <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{filterStore.endDate ? format(parseISO(filterStore.endDate), 'MMM d, yyyy') : 'End'}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filterStore.endDate ? parseISO(filterStore.endDate) : undefined}
                        onSelect={(date) => filterStore.setDateRange(
                          filterStore.startDate,
                          date ? format(date, 'yyyy-MM-dd') : null
                        )}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {/* Quick Date Selection - Moved to full width below */}
              </div>

              {/* Branch/Store Filter */}
              <MultiSelectFilter
                label="Branch"
                options={stores.map(s => ({ id: s.id, name: s.name }))}
                selected={filterStore.storeIds}
                onChange={filterStore.setStoreIds}
                testId="store-filter"
              />

              {/* POS Device Filter */}
              <MultiSelectFilter
                label="POS Device"
                options={posDevices.map(p => ({ id: p.id, name: p.name || `POS ${p.id.slice(-4)}` }))}
                selected={filterStore.posDeviceIds}
                onChange={filterStore.setPosDeviceIds}
                testId="pos-filter"
              />

              {/* Employee Filter */}
              <MultiSelectFilter
                label="Employee"
                options={employees.map(e => ({ id: e.id, name: e.name }))}
                selected={filterStore.employeeIds}
                onChange={filterStore.setEmployeeIds}
                testId="employee-filter"
              />
            </div>

            {/* Quick Date Shortcuts - Full width row */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  const yesterday = subDays(new Date(), 1);
                  filterStore.setDateRange(format(yesterday, 'yyyy-MM-dd'), format(yesterday, 'yyyy-MM-dd'));
                }}
                data-testid="quick-date-yesterday"
              >
                Yesterday
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  const today = new Date();
                  filterStore.setDateRange(format(today, 'yyyy-MM-dd'), format(today, 'yyyy-MM-dd'));
                }}
                data-testid="quick-date-today"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  const lastWeekStart = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
                  const lastWeekEnd = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
                  filterStore.setDateRange(format(lastWeekStart, 'yyyy-MM-dd'), format(lastWeekEnd, 'yyyy-MM-dd'));
                }}
                data-testid="quick-date-last-week"
              >
                Last Week
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
                  const lastMonthEnd = endOfMonth(subMonths(new Date(), 1));
                  filterStore.setDateRange(format(lastMonthStart, 'yyyy-MM-dd'), format(lastMonthEnd, 'yyyy-MM-dd'));
                }}
                data-testid="quick-date-last-month"
              >
                Last Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  const lastYearStart = startOfYear(subYears(new Date(), 1));
                  const lastYearEnd = endOfYear(subYears(new Date(), 1));
                  filterStore.setDateRange(format(lastYearStart, 'yyyy-MM-dd'), format(lastYearEnd, 'yyyy-MM-dd'));
                }}
                data-testid="quick-date-last-year"
              >
                Last Year
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  const now = new Date();
                  const thisMonthStart = startOfMonth(now);
                  filterStore.setDateRange(format(thisMonthStart, 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd'));
                }}
                data-testid="quick-date-this-month"
              >
                This Month
              </Button>
            </div>

            {/* Row 2: Category, Item, Modifier, Payment Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category Filter */}
              <MultiSelectFilter
                label="Category"
                options={categories.map(c => ({ id: c.id, name: c.name }))}
                selected={filterStore.categoryIds}
                onChange={filterStore.setCategoryIds}
                testId="category-filter"
              />

              {/* Item Filter */}
              <MultiSelectFilter
                label="Item"
                options={items.map(i => ({ id: i.id, name: i.item_name || i.name }))}
                selected={filterStore.itemIds}
                onChange={filterStore.setItemIds}
                testId="item-filter"
              />

              {/* Modifier Filter */}
              <MultiSelectFilter
                label="Modifier"
                options={modifiers.map(m => ({ id: m.id, name: m.name }))}
                selected={filterStore.modifierIds}
                onChange={filterStore.setModifierIds}
                testId="modifier-filter"
              />

              {/* Payment Type Filter */}
              <MultiSelectFilter
                label="Payment Type"
                options={paymentTypes.map(p => ({ id: p.id, name: p.name }))}
                selected={filterStore.paymentTypeIds}
                onChange={filterStore.setPaymentTypeIds}
                testId="payment-type-filter"
              />
            </div>

            {/* Row 3: Discount, Time Range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Discount Filter */}
              <MultiSelectFilter
                label="Discount"
                options={discounts.map(d => ({ id: d.id, name: d.name }))}
                selected={filterStore.discountIds}
                onChange={filterStore.setDiscountIds}
                testId="discount-filter"
              />

              {/* Time Range */}
              <div className="space-y-2">
                <Label>Time Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="time"
                    value={filterStore.startTime || ''}
                    onChange={(e) => filterStore.setTimeRange(e.target.value || null, filterStore.endTime)}
                    className="flex-1"
                    data-testid="start-time"
                  />
                  <Input
                    type="time"
                    value={filterStore.endTime || ''}
                    onChange={(e) => filterStore.setTimeRange(filterStore.startTime, e.target.value || null)}
                    className="flex-1"
                    data-testid="end-time"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end pt-4 border-t">
              <div className="flex gap-2">
                <Button variant="outline" onClick={filterStore.resetFilters} data-testid="reset-filters">
                  Reset
                </Button>
                <Button onClick={handleApplyFilters} data-testid="apply-filters">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <Card className="dashboard-card" data-testid="stat-transactions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatNumber(dashboardData?.total_transactions || 0)}
                  </p>
                  {comparisonData && (
                    <ComparisonBadge
                      current={dashboardData?.total_transactions}
                      previous={comparisonData?.total_transactions}
                    />
                  )}
                </div>
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-coffee-espresso" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card" data-testid="stat-quantity">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Quantity</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatNumber(dashboardData?.total_quantity || 0, 2)}
                  </p>
                  {comparisonData && (
                    <ComparisonBadge
                      current={dashboardData?.total_quantity}
                      previous={comparisonData?.total_quantity}
                    />
                  )}
                </div>
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                  <Package className="w-6 h-6 text-coffee-espresso" />
                </div>
              </div>
            </CardContent>
          </Card>

          {canViewRevenue() && (
            <>
              <Card className="dashboard-card" data-testid="stat-revenue">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Net Revenue</p>
                      <p className="text-2xl font-bold mt-1">
                        R{formatNumber(dashboardData?.total_revenue || 0, 2)}
                      </p>
                      {comparisonData && (
                        <ComparisonBadge
                          current={dashboardData?.total_revenue}
                          previous={comparisonData?.total_revenue}
                        />
                      )}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="stat-refunds">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Refunds</p>
                      <p className="text-2xl font-bold mt-1 text-red-600">
                        R{formatNumber(dashboardData?.total_refunds || 0, 2)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {dashboardData?.refund_count || 0} transactions
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <RotateCcw className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="stat-discounts">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Discounts</p>
                      <p className="text-2xl font-bold mt-1 text-orange-600">
                        R{formatNumber(dashboardData?.total_discounts || 0, 2)}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Percent className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dashboard-card" data-testid="stat-avg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Transaction</p>
                      <p className="text-2xl font-bold mt-1">
                        R{formatNumber(dashboardData?.average_transaction || 0, 2)}
                      </p>
                      {comparisonData && (
                        <ComparisonBadge
                          current={dashboardData?.average_transaction}
                          previous={comparisonData?.average_transaction}
                        />
                      )}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                      <Users className="w-6 h-6 text-coffee-espresso" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* View Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Label>View:</Label>
          <Tabs value={filterStore.viewType} onValueChange={filterStore.setViewType}>
            <TabsList>
              <TabsTrigger value="table" data-testid="view-table">
                <BarChart3 className="w-4 h-4 mr-1" />
                Table
              </TabsTrigger>
              <TabsTrigger value="chart" data-testid="view-chart">
                <LineChartIcon className="w-4 h-4 mr-1" />
                Chart
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {filterStore.viewType === 'chart' && (
          <>
            <div className="flex items-center gap-2">
              <Label>Chart Type:</Label>
              <Select value={filterStore.chartType} onValueChange={filterStore.setChartType}>
                <SelectTrigger className="w-32" data-testid="chart-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {targets.length > 0 && (
              <div className="flex items-center gap-2">
                <Label>Target Overlay:</Label>
                <Select 
                  value={selectedTarget?.id || 'none'} 
                  onValueChange={(v) => setSelectedTarget(v === 'none' ? null : targets.find(t => t.id === v))}
                >
                  <SelectTrigger className="w-40" data-testid="target-overlay-select">
                    <SelectValue placeholder="Select target" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Target</SelectItem>
                    {targets.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {stores.find(s => s.id === t.store_id)?.name || 'All Branches'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}

        <div className="flex items-center gap-2">
          <Label>Group By:</Label>
          <Select value={filterStore.groupBy} onValueChange={filterStore.setGroupBy}>
            <SelectTrigger className="w-32" data-testid="group-by-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {canViewRevenue() && (
          <div className="flex items-center gap-2">
            <Label>Report:</Label>
            <Select value={filterStore.reportType} onValueChange={filterStore.setReportType}>
              <SelectTrigger className="w-32" data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="quantity">Quantity</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Split View Toggle */}
        {filterStore.viewType === 'chart' && (filterStore.storeIds.length > 1 || !filterStore.storeIds.length) && (
          <div className="flex items-center gap-2">
            <Switch
              id="split-view"
              checked={filterStore.splitView}
              onCheckedChange={filterStore.setSplitView}
            />
            <Label htmlFor="split-view" className="flex items-center gap-1 cursor-pointer">
              <Split className="w-4 h-4" />
              Split by Branch
            </Label>
          </div>
        )}

        {/* Period Comparison Button */}
        <Dialog open={showComparisonDialog} onOpenChange={setShowComparisonDialog}>
          <DialogTrigger asChild>
            <Button
              variant={filterStore.comparisonEnabled ? "default" : "outline"}
              className="rounded-xl"
            >
              <GitCompare className="w-4 h-4 mr-2" />
              {filterStore.comparisonEnabled ? "Comparing Periods" : "Compare Periods"}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GitCompare className="w-5 h-5" />
                Period Comparison
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-1">Current Period</p>
                <p className="text-blue-900">
                  {filterStore.startDate && filterStore.endDate
                    ? `${format(parseISO(filterStore.startDate), 'MMM d, yyyy')} - ${format(parseISO(filterStore.endDate), 'MMM d, yyyy')}`
                    : 'Not set'}
                </p>
              </div>
              
              <div className="space-y-3">
                <Label className="text-base font-semibold">Compare with:</Label>
                
                {/* Quick Selection Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!filterStore.startDate || !filterStore.endDate}
                    onClick={() => {
                      if (!filterStore.startDate || !filterStore.endDate) return;
                      const start = parseISO(filterStore.startDate);
                      const end = parseISO(filterStore.endDate);
                      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                      filterStore.setComparisonDateRange(
                        format(subDays(start, days + 1), 'yyyy-MM-dd'),
                        format(subDays(start, 1), 'yyyy-MM-dd')
                      );
                      filterStore.setComparisonEnabled(true);
                    }}
                  >
                    Previous Period
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!filterStore.startDate || !filterStore.endDate}
                    onClick={() => {
                      if (!filterStore.startDate || !filterStore.endDate) return;
                      const start = parseISO(filterStore.startDate);
                      const end = parseISO(filterStore.endDate);
                      filterStore.setComparisonDateRange(
                        format(subMonths(start, 1), 'yyyy-MM-dd'),
                        format(subMonths(end, 1), 'yyyy-MM-dd')
                      );
                      filterStore.setComparisonEnabled(true);
                    }}
                  >
                    Same Period Last Month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!filterStore.startDate || !filterStore.endDate}
                    onClick={() => {
                      if (!filterStore.startDate || !filterStore.endDate) return;
                      const start = parseISO(filterStore.startDate);
                      const end = parseISO(filterStore.endDate);
                      filterStore.setComparisonDateRange(
                        format(new Date(start.getFullYear() - 1, start.getMonth(), start.getDate()), 'yyyy-MM-dd'),
                        format(new Date(end.getFullYear() - 1, end.getMonth(), end.getDate()), 'yyyy-MM-dd')
                      );
                      filterStore.setComparisonEnabled(true);
                    }}
                  >
                    Same Period Last Year
                  </Button>
                </div>
                
                {/* Custom Date Range */}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <Label className="text-sm text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={filterStore.comparisonStartDate || ''}
                      onChange={(e) => filterStore.setComparisonDateRange(e.target.value, filterStore.comparisonEndDate)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={filterStore.comparisonEndDate || ''}
                      onChange={(e) => filterStore.setComparisonDateRange(filterStore.comparisonStartDate, e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {filterStore.comparisonStartDate && filterStore.comparisonEndDate && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-800 mb-1">Comparison Period</p>
                    <p className="text-green-900">
                      {format(parseISO(filterStore.comparisonStartDate), 'MMM d, yyyy')} - {format(parseISO(filterStore.comparisonEndDate), 'MMM d, yyyy')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              {filterStore.comparisonEnabled && (
                <Button
                  variant="outline"
                  onClick={() => {
                    filterStore.setComparisonEnabled(false);
                    filterStore.setComparisonDateRange(null, null);
                    fetchDashboardData(true);
                    setShowComparisonDialog(false);
                  }}
                >
                  Clear Comparison
                </Button>
              )}
              <Button
                onClick={() => {
                  if (filterStore.comparisonStartDate && filterStore.comparisonEndDate) {
                    filterStore.setComparisonEnabled(true);
                    fetchDashboardData(true);
                    setShowComparisonDialog(false);
                  }
                }}
                disabled={!filterStore.comparisonStartDate || !filterStore.comparisonEndDate}
              >
                Apply Comparison
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Insights Button */}
        <Button
          variant="outline"
          onClick={handleGenerateInsights}
          disabled={loadingInsights || !dashboardData}
          className="rounded-xl"
        >
          {loadingInsights ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          AI Insights
        </Button>
      </div>

      {/* Comparison Summary Cards */}
      {filterStore.comparisonEnabled && comparisonData && dashboardData && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-green-600" />
              Period Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-lg font-bold">{formatNumber(dashboardData.total_transactions)}</p>
                <p className="text-xs text-muted-foreground">vs {formatNumber(comparisonData.total_transactions)}</p>
                {(() => {
                  const change = ((dashboardData.total_transactions - comparisonData.total_transactions) / comparisonData.total_transactions * 100);
                  return (
                    <Badge variant={change >= 0 ? "default" : "destructive"} className="mt-1">
                      {change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                      {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                    </Badge>
                  );
                })()}
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Quantity</p>
                <p className="text-lg font-bold">{formatNumber(dashboardData.total_quantity, 2)}</p>
                <p className="text-xs text-muted-foreground">vs {formatNumber(comparisonData.total_quantity, 2)}</p>
                {(() => {
                  const change = ((dashboardData.total_quantity - comparisonData.total_quantity) / comparisonData.total_quantity * 100);
                  return (
                    <Badge variant={change >= 0 ? "default" : "destructive"} className="mt-1">
                      {change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                      {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                    </Badge>
                  );
                })()}
              </div>
              {canViewRevenue() && (
                <>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-lg font-bold">R{formatNumber(dashboardData.total_revenue, 2)}</p>
                    <p className="text-xs text-muted-foreground">vs R{formatNumber(comparisonData.total_revenue, 2)}</p>
                    {(() => {
                      const change = ((dashboardData.total_revenue - comparisonData.total_revenue) / comparisonData.total_revenue * 100);
                      return (
                        <Badge variant={change >= 0 ? "default" : "destructive"} className="mt-1">
                          {change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                          {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Avg Transaction</p>
                    <p className="text-lg font-bold">R{formatNumber(dashboardData.average_transaction, 2)}</p>
                    <p className="text-xs text-muted-foreground">vs R{formatNumber(comparisonData.average_transaction, 2)}</p>
                    {(() => {
                      const change = ((dashboardData.average_transaction - comparisonData.average_transaction) / comparisonData.average_transaction * 100);
                      return (
                        <Badge variant={change >= 0 ? "default" : "destructive"} className="mt-1">
                          {change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                          {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                        </Badge>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Data View */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {filterStore.reportType === 'revenue' && canViewRevenue() ? 'Revenue' : 'Quantity'} by {filterStore.groupBy}
            </CardTitle>
            {selectedTarget && filterStore.viewType === 'chart' && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Target className="w-3 h-3" />
                Target: {filterStore.reportType === 'revenue' 
                  ? `R${formatNumber(selectedTarget.daily_revenue_avg, 2)}/day` 
                  : `${formatNumber(selectedTarget.daily_quantity_avg, 2)}/day`}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filterStore.viewType === 'chart' ? (
            <div className="h-80" data-testid="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                {filterStore.splitView && splitViewData ? (
                  // Split View Chart - Multiple lines/bars for each branch
                  filterStore.chartType === 'line' ? (
                    <LineChart data={splitViewData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDateLabel}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => formatNumber(v, 2)} />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                        formatter={(value, name) => [formatNumber(value, 2), name.replace('_qty', '').replace('_rev', '')]}
                      />
                      <Legend />
                      {splitViewStores.map((store, idx) => (
                        <Line
                          key={store.id}
                          type="monotone"
                          dataKey={filterStore.reportType === 'revenue' ? `${store.name}_rev` : `${store.name}_qty`}
                          stroke={chartColors[idx % chartColors.length]}
                          strokeWidth={2}
                          dot={{ fill: chartColors[idx % chartColors.length] }}
                          name={store.name}
                        />
                      ))}
                    </LineChart>
                  ) : (
                    <BarChart data={splitViewData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDateLabel}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => formatNumber(v, 2)} />
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                        formatter={(value, name) => [formatNumber(value, 2), name.replace('_qty', '').replace('_rev', '')]}
                      />
                      <Legend />
                      {splitViewStores.map((store, idx) => (
                        <Bar
                          key={store.id}
                          dataKey={filterStore.reportType === 'revenue' ? `${store.name}_rev` : `${store.name}_qty`}
                          fill={chartColors[idx % chartColors.length]}
                          name={store.name}
                        />
                      ))}
                    </BarChart>
                  )
                ) : filterStore.chartType === 'line' ? (
                  <LineChart data={groupedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDateLabel}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => formatNumber(v, 2)} />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value) => [formatNumber(value, 2), '']}
                    />
                    <Legend />
                    {selectedTarget && filterStore.groupBy === 'day' && (
                      <ReferenceLine 
                        y={filterStore.reportType === 'revenue' && canViewRevenue() 
                          ? selectedTarget.daily_revenue_avg 
                          : selectedTarget.daily_quantity_avg} 
                        stroke="#C3784A" 
                        strokeDasharray="5 5"
                        label={{ value: 'Target', fill: '#C3784A', fontSize: 12 }}
                      />
                    )}
                    {/* Comparison line for previous period */}
                    {groupedComparisonData.length > 0 && (
                      <Line
                        type="monotone"
                        data={groupedData.map((d, i) => ({
                          ...d,
                          prev_value: groupedComparisonData[i] 
                            ? (filterStore.reportType === 'revenue' ? groupedComparisonData[i].revenue : groupedComparisonData[i].quantity)
                            : null
                        }))}
                        dataKey="prev_value"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={1}
                        strokeDasharray="5 5"
                        dot={false}
                        name="Previous Period"
                        connectNulls
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey={filterStore.reportType === 'revenue' && canViewRevenue() ? 'revenue' : 'quantity'}
                      stroke="hsl(var(--chart-1))"
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--chart-1))' }}
                      name={filterStore.reportType === 'revenue' && canViewRevenue() ? 'Revenue (R)' : 'Quantity'}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={groupedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDateLabel}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => formatNumber(v, 2)} />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value) => [formatNumber(value, 2), '']}
                    />
                    <Legend />
                    {selectedTarget && filterStore.groupBy === 'day' && (
                      <ReferenceLine 
                        y={filterStore.reportType === 'revenue' && canViewRevenue() 
                          ? selectedTarget.daily_revenue_avg 
                          : selectedTarget.daily_quantity_avg} 
                        stroke="#C3784A" 
                        strokeDasharray="5 5"
                        label={{ value: 'Target', fill: '#C3784A', fontSize: 12 }}
                      />
                    )}
                    <Bar
                      dataKey={filterStore.reportType === 'revenue' && canViewRevenue() ? 'revenue' : 'quantity'}
                      fill="hsl(var(--chart-1))"
                      radius={[4, 4, 0, 0]}
                      name={filterStore.reportType === 'revenue' && canViewRevenue() ? 'Revenue (R)' : 'Quantity'}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="responsive-table" data-testid="data-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    {canViewRevenue() && <TableHead className="text-right">Revenue</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canViewRevenue() ? 3 : 2} className="text-center py-8 text-muted-foreground">
                        No data available for the selected filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupedData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {filterStore.groupBy === 'day' && format(parseISO(row.date), 'MMM d, yyyy')}
                          {filterStore.groupBy === 'week' && `Week of ${format(parseISO(row.date), 'MMM d, yyyy')}`}
                          {filterStore.groupBy === 'month' && format(parseISO(row.date + '-01'), 'MMMM yyyy')}
                          {filterStore.groupBy === 'year' && row.date}
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(row.quantity, 2)}</TableCell>
                        {canViewRevenue() && (
                          <TableCell className="text-right font-mono">
                            R{formatNumber(row.revenue, 2)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Items */}
      <Card>
        <CardHeader>
          <CardTitle>Top Selling Items</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="responsive-table" data-testid="top-items-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    {canViewRevenue() && <TableHead className="text-right">Revenue</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(dashboardData?.top_items || []).map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(item.quantity, 2)}</TableCell>
                      {canViewRevenue() && (
                        <TableCell className="text-right font-mono">
                          R{formatNumber(item.revenue, 2)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Insights Dialog */}
      <Dialog open={showInsights} onOpenChange={setShowInsights}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              AI Sales Insights
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {insights ? (
              <>
                {insights.summary && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" /> Executive Summary
                    </h4>
                    <p className="text-blue-900">{insights.summary}</p>
                  </div>
                )}
                
                {insights.trends && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Key Trends
                    </h4>
                    <p className="text-green-900">{insights.trends}</p>
                  </div>
                )}
                
                {insights.anomalies && (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" /> Notable Patterns
                    </h4>
                    <p className="text-yellow-900">{insights.anomalies}</p>
                  </div>
                )}
                
                {insights.recommendations && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> Recommendations
                    </h4>
                    <p className="text-purple-900">{insights.recommendations}</p>
                  </div>
                )}
                
                {insights.raw_response && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-900 whitespace-pre-wrap">{insights.raw_response}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInsights(false)}>
              Close
            </Button>
            <Button onClick={handleGenerateInsights} disabled={loadingInsights}>
              {loadingInsights ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Comparison Badge Component
const ComparisonBadge = ({ current, previous }) => {
  if (!previous || previous === 0) return null;
  
  const change = ((current - previous) / previous) * 100;
  const isPositive = change >= 0;
  
  return (
    <div className={`flex items-center gap-1 mt-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      <span>{isPositive ? '+' : ''}{change.toFixed(1)}%</span>
    </div>
  );
};

export default Dashboard;
